import React, { useState, useEffect } from 'react';
import { Settings, Save, Upload, Globe, User, Mail, MessageCircle, Plus, Trash2, Quote } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { api } from '../services/apiClient';

const SettingsManager = () => {
  const { settings, loading, updateSetting, updateMultipleSettings } = useSettings();
  const [formData, setFormData] = useState(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  async function handleImageUpload(e, fieldName) {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const compressedFile = await compressImage(file, 800, 800, 0.85);
      const data = await api.images.upload(compressedFile);

      const newFormData = { ...formData, [fieldName]: data.url };
      setFormData(newFormData);
      await updateSetting(fieldName, data.url);
      showSavedMessage();
    } catch (error) {
      alert(`Errore: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  async function compressImage(file, maxWidth, maxHeight, quality) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Errore caricamento immagine'));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error('Errore lettura file'));
      reader.readAsDataURL(file);
    });
  }

  function showSavedMessage() {
    setSavedMessage('Salvato con successo!');
    setTimeout(() => setSavedMessage(''), 3000);
  }

  function addTestimonial() {
    const current = formData.testimonials || [];
    handleInputChange('testimonials', [...current, { name: '', role: '', text: '' }]);
  }

  function removeTestimonial(idx) {
    const current = formData.testimonials || [];
    handleInputChange('testimonials', current.filter((_, i) => i !== idx));
  }

  function handleTestimonialChange(idx, field, value) {
    const current = [...(formData.testimonials || [])];
    current[idx] = { ...current[idx], [field]: value };
    handleInputChange('testimonials', current);
  }

  async function handleSaveAll() {
    setIsSaving(true);
    const success = await updateMultipleSettings(formData);
    if (success) {
      showSavedMessage();
    }
    setIsSaving(false);
  }

  const handleInputChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-dark"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-brand-dark mb-6">
        <Settings size={24} />
        <h3 className="text-xl font-semibold">Impostazioni Store</h3>
      </div>

      <div className="space-y-6">
        {/* SEZIONE SITO */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <Globe size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Informazioni Sito</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Nome Sito</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.site_name}
                onChange={e => handleInputChange('site_name', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Logo Sito</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg border overflow-hidden">
                  <img src={formData.site_logo_url} alt="Logo" className="w-full h-full object-cover" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer bg-brand-light px-4 py-2 rounded-lg hover:bg-brand-main hover:text-white transition-all text-sm font-medium">
                  <Upload size={16} />
                  {uploading ? 'Caricamento...' : 'Carica Logo'}
                  <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, 'site_logo_url')} disabled={uploading} />
                </label>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_prices}
                  onChange={e => handleInputChange('show_prices', e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm font-medium text-gray-600">Mostra prezzi nel negozio</span>
              </label>
            </div>
          </div>
        </div>

        {/* SEZIONE HOMEPAGE */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <Globe size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Contenuti Homepage</h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Titolo Hero</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.hero_tagline || ''}
                onChange={e => handleInputChange('hero_tagline', e.target.value)}
                placeholder="Es. Arte che abita i tuoi spazi"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Sottotitolo Hero <span className="text-gray-400 font-normal">(lascia vuoto per nasconderlo)</span></label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                rows="3"
                value={formData.hero_description || ''}
                onChange={e => handleInputChange('hero_description', e.target.value)}
                placeholder="Es. Scopri una collezione curata..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Testo sezione "Chi sono" (Homepage)</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                rows="4"
                value={formData.about_snippet || ''}
                onChange={e => handleInputChange('about_snippet', e.target.value)}
                placeholder="Breve presentazione mostrata nella homepage..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Titolo CTA finale</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.cta_title || ''}
                onChange={e => handleInputChange('cta_title', e.target.value)}
                placeholder="Es. Pronto a scoprire?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Sottotitolo CTA finale</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.cta_subtitle || ''}
                onChange={e => handleInputChange('cta_subtitle', e.target.value)}
                placeholder="Es. Esplora la collezione completa e trova l'opera perfetta per te."
              />
            </div>
          </div>
        </div>

        {/* SEZIONE ARTISTA */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <User size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Profilo Artista</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Nome Artista</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.artist_name}
                onChange={e => handleInputChange('artist_name', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Foto Artista</label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-lg border overflow-hidden">
                  <img src={formData.artist_photo_url} alt="Artista" className="w-full h-full object-cover" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer bg-brand-light px-4 py-2 rounded-lg hover:bg-brand-main hover:text-white transition-all text-sm font-medium">
                  <Upload size={16} />
                  {uploading ? 'Caricamento...' : 'Carica Foto'}
                  <input type="file" className="hidden" accept="image/*" onChange={e => handleImageUpload(e, 'artist_photo_url')} disabled={uploading} />
                </label>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Bio Breve</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.artist_bio}
                onChange={e => handleInputChange('artist_bio', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">La Tua Storia (About)</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                rows="4"
                value={formData.about_story}
                onChange={e => handleInputChange('about_story', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Label sezione Filosofia (About)</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.about_philosophy_label || ''}
                onChange={e => handleInputChange('about_philosophy_label', e.target.value)}
                placeholder="Es. La Mia Filosofia"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Filosofia Artistica</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                rows="4"
                value={formData.about_philosophy}
                onChange={e => handleInputChange('about_philosophy', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* SEZIONE TESTIMONIANZE */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <Quote size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Testimonianze e Citazioni</h4>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-2">Label sezione <span className="text-gray-400 font-normal">(lascia vuoto per nascondere solo il titolo)</span></label>
            <input
              type="text"
              className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
              value={formData.testimonials_label || ''}
              onChange={e => handleInputChange('testimonials_label', e.target.value)}
              placeholder="Es. Testimonianze e Citazioni"
            />
          </div>

          <div className="space-y-3">
            {(formData.testimonials || []).map((t, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-2 relative">
                <button
                  type="button"
                  onClick={() => removeTestimonial(idx)}
                  className="absolute top-3 right-3 text-red-400 hover:text-red-600 transition-colors"
                  title="Rimuovi"
                >
                  <Trash2 size={16} />
                </button>
                <input
                  type="text"
                  placeholder="Nome autore"
                  className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none text-sm"
                  value={t.name || ''}
                  onChange={e => handleTestimonialChange(idx, 'name', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Ruolo / contesto (opzionale)"
                  className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none text-sm"
                  value={t.role || ''}
                  onChange={e => handleTestimonialChange(idx, 'role', e.target.value)}
                />
                <textarea
                  placeholder="Testo della testimonianza o citazione..."
                  className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none text-sm"
                  rows="3"
                  value={t.text || ''}
                  onChange={e => handleTestimonialChange(idx, 'text', e.target.value)}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={addTestimonial}
              className="flex items-center gap-2 text-sm text-brand-dark border border-brand-main px-4 py-2 rounded-lg hover:bg-brand-light transition-colors"
            >
              <Plus size={16} />
              Aggiungi testimonianza
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-3">Le testimonianze appaiono nella pagina "Chi sono" sotto la sezione filosofia.</p>
        </div>

        {/* SEZIONE CONTATTI */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <Mail size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Contatti</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
              <input
                type="email"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.contact_email}
                onChange={e => handleInputChange('contact_email', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Telefono</label>
              <input
                type="tel"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.contact_phone}
                onChange={e => handleInputChange('contact_phone', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Indirizzo</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.contact_address}
                onChange={e => handleInputChange('contact_address', e.target.value)}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Titolo riquadro finale (pagina Contatti)</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.contact_cta_title || ''}
                onChange={e => handleInputChange('contact_cta_title', e.target.value)}
                placeholder="Es. Inizia una conversazione"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-2">Testo riquadro finale (pagina Contatti)</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                rows="3"
                value={formData.contact_cta_text || ''}
                onChange={e => handleInputChange('contact_cta_text', e.target.value)}
                placeholder="Es. Che tu sia interessato alle mie opere..."
              />
            </div>
          </div>
        </div>

        {/* SEZIONE SOCIAL */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <MessageCircle size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Social Media</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['instagram', 'facebook', 'whatsapp', 'telegram', 'tiktok', 'youtube'].map(social => (
              <div key={social}>
                <label className="block text-sm font-medium text-gray-600 mb-2 capitalize">{social}</label>
                <input
                  type="text"
                  placeholder={`Link o numero ${social}`}
                  className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none text-sm"
                  value={formData[`social_${social}`] || ''}
                  onChange={e => handleInputChange(`social_${social}`, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* SEZIONE CITAZIONE */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <MessageCircle size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Citazione Homepage</h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Testo Citazione</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none font-mono text-sm"
                rows="4"
                value={formData.quote_text || ''}
                onChange={e => handleInputChange('quote_text', e.target.value)}
                placeholder="Es. L'arte non è ciò che vedi,&#10;ma **ciò che fai vedere** agli altri.&#10;(premi Invio per andare a capo)"
              />
              <div className="text-xs text-gray-500 mt-2 space-y-1">
                <p>💡 <code className="bg-gray-100 px-1 rounded">**parola**</code> per il grassetto</p>
                <p>💡 Premi <kbd className="bg-gray-100 px-1 rounded">Invio</kbd> per andare a capo</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Autore</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.quote_author || ''}
                onChange={e => handleInputChange('quote_author', e.target.value)}
                placeholder="Es. Edgar Degas"
              />
            </div>
          </div>
        </div>

        {/* SEZIONE FOOTER INFO */}
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
            <Globe size={20} className="text-brand-main" />
            <h4 className="text-lg font-semibold text-brand-dark">Info & Orari (Footer)</h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Titolo colonna</label>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none"
                value={formData.footer_info_title || ''}
                onChange={e => handleInputChange('footer_info_title', e.target.value)}
                placeholder="Es. Info & Orari"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Contenuto (una riga per voce)</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-main outline-none text-sm"
                rows="10"
                value={formData.footer_info || ''}
                onChange={e => handleInputChange('footer_info', e.target.value)}
                placeholder="P.IVA: ...&#10;Orari: ..."
              />
              <div className="text-xs text-gray-400 mt-2 space-y-0.5">
                <p>Premi Invio per andare a capo. Formattazione supportata:</p>
                <p><code className="bg-gray-100 px-1 rounded">**testo**</code> → <strong>grassetto</strong></p>
                <p><code className="bg-gray-100 px-1 rounded">__testo__</code> → <u>sottolineato</u></p>
                <p><code className="bg-gray-100 px-1 rounded">- voce</code> → • elenco puntato</p>
              </div>
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        {savedMessage && (
          <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm border border-green-200 flex items-center gap-2">
            <Save size={16} />
            {savedMessage}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={handleSaveAll}
            disabled={isSaving}
            className="flex items-center gap-2 bg-brand-dark text-white px-6 py-3 rounded-lg hover:bg-opacity-90 disabled:opacity-50 transition-all font-medium"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Salvataggio...
              </>
            ) : (
              <>
                <Save size={18} />
                Salva Tutto
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-brand-light p-6 rounded-xl border border-brand-main">
        <h5 className="font-semibold text-brand-dark mb-3">💡 Note</h5>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Le modifiche vengono salvate immediatamente nel database</li>
          <li>• Gli utenti vedranno i cambiamenti al prossimo refresh della pagina</li>
          <li>• Puoi lasciare vuoti i campi social che non usi</li>
          <li>• Tutte le immagini vengono compresse automaticamente per il web</li>
        </ul>
      </div>
    </div>
  );
};

export default SettingsManager;
